/**
 * @fileoverview Provides a utility function for moving files within the current working directory, with input validation and safety checks. The module exports a TypeScript function and a tool definition for moving files, supporting both absolute and relative path specifications.
 */

import { ToolDefinition } from '@iterator/shared-node/ai/llm_types'
import * as fs from 'fs'
import * as path from 'path'
import { z } from 'zod'

export const MoveFileArgs = z.object({
  sourcePath: z.string(),
  destinationPath: z.string(),
})
export type MoveFileArgs = z.infer<typeof MoveFileArgs>
export type MoveFileCall = { name: 'moveFile'; args: MoveFileArgs }

export function moveFile(sourcePath: string, destinationPath: string): string {
  if (typeof sourcePath !== 'string' || sourcePath.trim() === '') {
    throw new Error('Invalid sourcePath: must be a non-empty string')
  }

  if (typeof destinationPath !== 'string' || destinationPath.trim() === '') {
    throw new Error('Invalid destinationPath: must be a non-empty string')
  }

  const absoluteSourcePath = path.isAbsolute(sourcePath)
    ? sourcePath
    : path.join(process.cwd(), sourcePath)
  const absoluteDestinationPath = path.isAbsolute(destinationPath)
    ? destinationPath
    : path.join(process.cwd(), destinationPath)

  if (
    !absoluteSourcePath.startsWith(process.cwd()) ||
    !absoluteDestinationPath.startsWith(process.cwd())
  ) {
    throw new Error(
      'Invalid path: both sourcePath and destinationPath must be within the current working directory or its subdirectories',
    )
  }

  if (!fs.existsSync(absoluteSourcePath)) {
    throw new Error(`Source file does not exist: ${sourcePath}`)
  }

  fs.renameSync(absoluteSourcePath, absoluteDestinationPath)

  return `Moved file from ${sourcePath} to ${destinationPath}`
}

export const moveFileTool: ToolDefinition = {
  name: 'moveFile',
  function: {
    name: 'moveFile',
    description:
      'Moves a file from the source path to the destination path. This can be used to rename files.',
    parameters: {
      type: 'object',
      properties: {
        sourcePath: {
          type: 'string',
          description: 'The path to the source file',
        },
        destinationPath: {
          type: 'string',
          description: 'The path to the destination file',
        },
      },
      required: ['sourcePath', 'destinationPath'],
    },
  },
  execute: async (args: unknown): Promise<string> => {
    const { sourcePath, destinationPath } = MoveFileArgs.parse(args)
    return moveFile(sourcePath, destinationPath)
  },
}
